import * as React from 'react';
import {
    SimpleForm,
    TextInput,
    required,
    ReferenceInput,
    SelectInput,
    SimpleFormIterator,
    BooleanInput,
    ArrayInput,
    useNotify,
    AutocompleteInput,
    useSimpleFormIteratorItem,
    useRedirect,
    NumberInput,
    Toolbar,
    SaveButton,
} from 'react-admin';
import { useParams } from 'react-router';
import HTMLEditorInput, { validHtml } from '../../common/inputs/HTMLEditorInput';
import { QUESTION_TYPES } from '../../common/constants';
import { Box, Tab, Tabs, Checkbox, ListItemText, MenuItem, Select, Button } from '@mui/material';
import FileUpload from '../../common/inputs/FileUpload';
import { TabPanel } from '../batches/BatchesShow';
import { goApi, codesToNames, getLanguageMap } from '../../services';
import { apiRoutes } from '../../services/apiRoutes';
import { MasterDataAutoComplete } from '../../common/inputs/autoCompleteInputs/MasterDataAutoComplete';
import { filterToQueryVideo, videoNameRender } from './QuestionBankCreate';
import { LoadingSection } from '../../common/LoadingSection';

const EXCLUDED_LANG_FIELD_KEYS = ['id', 'language'];
const EXCLUDED_OPTION_KEYS = [
    'id',
    'language',
    'optionId',
    'optionIndex',
    'isCorrect',
    'displayOrder',
];

function getLangFormFields(questionLangArr: any[] = []) {
    const fields: any = {};

    if (!questionLangArr.length) return fields;

    questionLangArr.forEach(langObj => {
        if (!langObj?.language) return;
        Object.keys(langObj).forEach(key => {
            if (!EXCLUDED_LANG_FIELD_KEYS.includes(key)) {
                fields[`${langObj.language} ${key}`] = langObj[key];
            }
        });
    });

    return fields;
}

function getLangOptions(optionLangArr: any[] = [], defaultOptions: any[] = []) {
    const fields: any = {};

    if (!optionLangArr.length) return fields;

    const groupedByLang: Record<string, any[]> = {};
    optionLangArr.forEach(opt => {
        const lang = opt.language;
        if (!groupedByLang[lang]) groupedByLang[lang] = [];
        groupedByLang[lang].push(opt);
    });

    Object.entries(groupedByLang).forEach(([lang, list]) => {
        list.forEach((opt, idx) => {
            let index = opt.optionIndex ?? idx;

            if (opt.optionId && defaultOptions) {
                const found = defaultOptions.findIndex((d: any) => d.optionId === opt.optionId);
                if (found > -1) index = found;
            }

            Object.keys(opt).forEach(key => {
                if (!EXCLUDED_OPTION_KEYS.includes(key)) {
                    const fieldName = `option-${index} ${lang} ${key}`;
                    fields[fieldName] = opt[key];
                }
            });
        });
    });

    return fields;
}

async function transformData(data: any, languages: string[], originalQuestionDetails: any) {
    const formData = { ...data };

    // Validation
    const correctOptions = formData.options?.filter((opt: any) => opt.isCorrect) || [];

    if (formData.type === 'SINGLE' && correctOptions.length !== 1) {
        throw new Error('Exactly one correct answer should be selected');
    }

    if (formData.type === 'MULTIPLE' && !correctOptions.length) {
        throw new Error('At least one correct answer should be selected');
    }

    // Extract language-specific option fields
    const languagesOptions: Record<string, any[]> = {};

    languages.forEach(language => {
        languagesOptions[language] = (formData.options || []).map((option: any, index: number) => {
            const prefix = `option-${index} ${language} `;
            const optionFields: any = {};

            Object.keys(formData).forEach(key => {
                if (key.startsWith(prefix)) {
                    optionFields[key.replace(prefix, '')] = formData[key];
                    delete formData[key];
                }
            });

            return { ...option, ...optionFields, id: option.id };
        });
    });

    // Build langQuestions array
    const langQuestions = languages.map(language => {
        const originalLangQuestion = originalQuestionDetails?.questionLang?.find(
            (lq: any) => lq.language === language,
        );

        // Merge options with original metadata
        const options =
            languagesOptions[language]?.map((opt: any, index: number) => {
                const originalOption = originalLangQuestion?.options?.[index];
                // Keep the language-specific id from original, remove language field
                const { language: _optLang, ...originalOptionWithoutLang } = originalOption || {};
                return { ...originalOptionWithoutLang, ...opt, id: originalOption?.id || opt.id };
            }) || [];

        // Extract language-specific fields from formData
        const langSpecificFields: any = {};
        Object.keys(formData).forEach(key => {
            if (key.startsWith(`${language} `)) {
                const fieldName = key.replace(`${language} `, '');
                langSpecificFields[fieldName] = formData[key];
                delete formData[key];
            }
        });

        // Remove language field from originalLangQuestion to avoid duplication
        const { language: _lang, ...originalWithoutLang } = originalLangQuestion || {};

        const langQuestion = {
            ...originalWithoutLang,
            ...langSpecificFields,
            questionId: formData.id,
            options,
            language: language, // Use the full language name (English, Hindi, etc.)
        };

        return langQuestion;
    });

    return {
        id: formData.id,
        type: formData.type,
        positiveMarks: formData.positiveMarks,
        negativeMarks: formData.negativeMarks,
        masterClassId: formData.masterClassId,
        masterSubjectId: formData.masterSubjectId,
        masterChapterId: formData.masterChapterId,
        masterTopicId: formData.masterTopicId,
        languages: langQuestions,
    };
}

const LanguageFields = React.memo(({ language }: { language: string }) => (
    <>
        <FileUpload source="image" type="VIDEO_THUMBNAIL" typeId="" />
        <TextInput source={`${language} name`} fullWidth />
        <HTMLEditorInput source={`${language} content`} fullWidth />
        <HTMLEditorInput source={`${language} solutionDescription`} fullWidth />
        <ReferenceInput name="Video" source={`${language} video`} reference="videos">
            <AutocompleteInput
                optionText={videoNameRender}
                filterToQuery={filterToQueryVideo}
                limitChoicesToValue
                debounce={500}
                fullWidth
            />
        </ReferenceInput>
        <FileUpload source="solutionImage" type="VIDEO_THUMBNAIL" typeId="" />
    </>
));

const OptionUI = (props: any) => {
    const { index } = useSimpleFormIteratorItem();
    const { selectedType, lang } = props;

    if (!selectedType) return null;

    const contentFieldName = `option-${index} ${lang} content`;
    const imageFieldName = `option-${index} ${lang} image`;
    const solutionTextFieldName = `option-${index} ${lang} solutionText`;

    if (selectedType === 'NUMERIC') {
        return <TextInput fullWidth source={solutionTextFieldName} type="number" />;
    }

    return (
        <>
            <FileUpload source={imageFieldName} label="image" type="VIDEO_THUMBNAIL" typeId="" />
            <HTMLEditorInput
                label="Content"
                fullWidth
                source={contentFieldName}
                validate={[validHtml]}
            />
        </>
    );
};

const OptionsFields = ({
    languages,
    selectedType,
}: {
    languages: string[];
    selectedType: string;
}) => {
    const [value, setValue] = React.useState(0);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <ArrayInput
                source="options"
                sx={{
                    border: '1px solid #aaaaaa',
                    padding: '10px',
                    borderRadius: '10px',
                    marginBottom: '20px',
                }}
            >
                <SimpleFormIterator source="options">
                    <BooleanInput
                        defaultValue={false}
                        fullWidth
                        label="Is Correct"
                        source="isCorrect"
                    />
                    <NumberInput
                        fullWidth
                        source={`displayOrder`}
                        label="Display order"
                        defaultValue={0}
                    />
                    <Tabs value={value} onChange={handleChange}>
                        {languages.map(lang => (
                            <Tab key={lang} label={lang} />
                        ))}
                    </Tabs>
                    {languages.map((lang, index) => (
                        <TabPanel key={lang} value={value} index={index}>
                            <OptionUI selectedType={selectedType} lang={lang} index={index} />
                        </TabPanel>
                    ))}
                </SimpleFormIterator>
            </ArrayInput>
        </Box>
    );
};

const QuestionBankEdit = ({
    questionId: propQuestionId,
    onClose,
}: { questionId?: string; onClose?: () => void } = {}) => {
    const { questionId: paramQuestionId } = useParams<{ questionId: string }>();
    const questionId = propQuestionId || paramQuestionId;
    const notify = useNotify();
    const redirect = useRedirect();

    const [questionDetails, setQuestionDetails] = React.useState<any>(null);
    const [languagesList, setLanguagesList] = React.useState<string[]>([]);
    const [selectedLanguages, setSelectedLanguages] = React.useState<string[]>(['English']);
    const [value, setValue] = React.useState(0);
    const [selectedMasterClass, setSelectedMasterClass] = React.useState('');
    const [selectedMasterSubject, setSelectedMasterSubject] = React.useState('');
    const [selectedMasterChapter, setSelectedMasterChapter] = React.useState('');
    const [selectedMasterTopic, setSelectedMasterTopic] = React.useState('');

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const [langResp, questionResp] = await Promise.all([
                    getLanguageMap(),
                    fetchQuestionDetails(questionId),
                ]);

                const langNames = langResp ? Object.keys(langResp) : ['English'];
                setLanguagesList(langNames);

                if (!questionResp?.data) {
                    setSelectedLanguages(
                        langNames.includes('English') ? ['English'] : [langNames[0]],
                    );
                    return;
                }

                const { data } = questionResp;
                const langFields = getLangFormFields(data.questionLang || []);
                const optionFields = getLangOptions(data.optionLang || [], data.options || []);

                const mergedQuestion = {
                    ...data.question,
                    ...langFields,
                    ...optionFields,
                    options: data.options || [],
                };

                setQuestionDetails({ ...data, question: mergedQuestion });

                // Initialize master fields state
                const { question } = data;
                setSelectedMasterClass(question.masterClassId || '');
                setSelectedMasterSubject(question.masterSubjectId || '');
                setSelectedMasterChapter(question.masterChapterId || '');
                setSelectedMasterTopic(question.masterTopicId || '');

                // Set selected languages
                if (data.questionLang?.length > 0) {
                    const langs = data.questionLang
                        .map((l: any) => l.language)
                        .sort((a: string, b: string) => {
                            if (a === 'English') return -1;
                            if (b === 'English') return 1;
                            return a.localeCompare(b);
                        });
                    setSelectedLanguages(langs);
                } else {
                    setSelectedLanguages(
                        langNames.includes('English') ? ['English'] : [langNames[0]],
                    );
                }
            } catch (err) {
                notify('Failed to load data', { type: 'warning' });
            }
        };

        fetchData();
    }, [questionId, notify]);

    const fetchQuestionDetails = React.useCallback(async (qId: string | undefined) => {
        if (!qId) return null;

        try {
            const response = await goApi.get(apiRoutes.QUESTIONS_BANK_DETAIL(qId));
            if (!response?.data) return null;

            const questionData = response.data;
            const { langQuestions = [] } = questionData;

            // Convert language codes to full names for the form
            const langCodes = langQuestions.map((lq: any) => lq.language);
            const langNames = await codesToNames(langCodes);
            const questionLang = langQuestions.map((lq: any, idx: number) => ({
                ...lq,
                language: langNames[idx],
            }));

            // First, get base question (English preferred) to establish option order
            const baseQuestion =
                langQuestions.find((lq: any) => lq.language === 'en') || langQuestions[0];
            const baseOptions =
                baseQuestion?.options?.map((opt: any, idx: number) => ({
                    id: opt.id,
                    optionId: opt.optionId,
                    isCorrect: opt.isCorrect || false,
                    displayOrder: opt.displayOrder || idx,
                })) || [];

            // Create a map of optionId -> index for consistent ordering
            const optionIdToIndex = new Map<string, number>();
            baseOptions.forEach((opt: any, idx: number) => {
                optionIdToIndex.set(opt.optionId, idx);
            });

            // Map all options with consistent indices based on optionId
            const optionLang = langQuestions.flatMap((lq: any, langIdx: number) =>
                (lq.options || []).map((opt: any) => {
                    const optId = opt.optionId || opt.id;
                    const optionIndex = optionIdToIndex.get(optId) ?? 0;

                    return {
                        language: langNames[langIdx],
                        content: opt.content,
                        image: opt.image,
                        solutionText: opt.solutionText,
                        optionId: optId,
                        optionIndex: optionIndex,
                        isCorrect: opt.isCorrect || opt.is_correct || false,
                        displayOrder: opt.displayOrder || opt.display_order || optionIndex,
                    };
                }),
            );

            return {
                data: {
                    question: {
                        id: questionData.id,
                        content: questionData.content,
                        name: questionData.name,
                        image: questionData.image,
                        type: questionData.type,
                        solutionDescription: questionData.solutionDescription,
                        solutionText: questionData.solutionText,
                        solutionImage: questionData.solutionImage,
                        video: questionData.videoId,
                        positiveMarks: questionData.positiveMarks,
                        negativeMarks: questionData.negativeMarks,
                        masterClassId: questionData.masterClassId,
                        masterSubjectId: questionData.masterSubjectId,
                        masterChapterId: questionData.masterChapterId,
                        masterTopicId: questionData.masterTopicId,
                    },
                    questionLang,
                    options: baseOptions,
                    optionLang,
                },
            };
        } catch (err) {
            return null;
        }
    }, []);

    const handleLanguagesChange = React.useCallback(
        (event: any) => {
            let value = event.target.value as string[];

            if (selectedLanguages.length <= 1 && value.length === 0) {
                notify('Cannot remove last language.', { type: 'warning' });
                return;
            }

            if (value.length > 1) {
                value = value.filter((lang: string) => lang !== 'Select Languages');
            }

            setSelectedLanguages(value);
        },
        [selectedLanguages.length, notify],
    );

    const handleChange = React.useCallback((event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    }, []);

    const modifiedPostSave = React.useCallback(
        async (data: any) => {
            try {
                if (!questionId) {
                    notify('Question ID is missing', { type: 'error' });
                    return;
                }
                const createData = await transformData(data, selectedLanguages, questionDetails);
                await goApi.put(apiRoutes.QUESTIONS_BANK_DETAIL(questionId), createData);
                notify('Updated question successfully', { type: 'success' });
                if (onClose) {
                    onClose();
                    window.location.reload();
                } else {
                    redirect('/questions_bank');
                }
            } catch (error: any) {
                notify(`Error: ${error?.message || 'Unknown error'}`, { type: 'warning' });
            }
        },
        [questionId, selectedLanguages, questionDetails, notify, redirect, onClose],
    );

    const CustomToolbar = () => (
        <Toolbar>
            <SaveButton />
            {onClose && (
                <Button onClick={onClose} sx={{ ml: 2 }} variant="outlined">
                    Cancel
                </Button>
            )}
        </Toolbar>
    );

    if (!questionDetails) {
        return (
            <div className="h-screen my-auto">
                <LoadingSection />
            </div>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <SimpleForm
                key={questionDetails?.question?.id}
                defaultValues={questionDetails.question}
                onSubmit={modifiedPostSave}
                toolbar={onClose ? <CustomToolbar /> : undefined}
            >
                <Box sx={{ width: '100%' }}>
                    <SelectInput
                        source="type"
                        label={'Type'}
                        fullWidth
                        disabled
                        validate={[required()]}
                        choices={QUESTION_TYPES}
                    />

                    <Select
                        multiple
                        placeholder="Select Languages"
                        value={selectedLanguages.length ? selectedLanguages : ['Select Languages']}
                        onChange={handleLanguagesChange}
                        renderValue={selected => (selected as string[]).join(', ')}
                        fullWidth
                        size="small"
                        className="mb-6"
                    >
                        {languagesList.map(lang => (
                            <MenuItem key={lang} value={lang} sx={{ py: 0.5, minWidth: 400 }}>
                                <Checkbox
                                    checked={selectedLanguages.indexOf(lang) > -1}
                                    size="small"
                                />
                                <ListItemText
                                    primary={lang}
                                    primaryTypographyProps={{ fontSize: 14 }}
                                />
                            </MenuItem>
                        ))}
                    </Select>

                    <NumberInput source={'positiveMarks'} fullWidth />
                    <NumberInput source={'negativeMarks'} fullWidth />

                    <MasterDataAutoComplete
                        key={`class-${questionDetails?.question?.masterClassId || 'empty'}`}
                        type="class"
                        onChange={(value: any) => setSelectedMasterClass(value || '')}
                    />

                    <MasterDataAutoComplete
                        key={`subject-${questionDetails?.question?.masterSubjectId || 'empty'}`}
                        type="subject"
                        onChange={(value: any) => {
                            setSelectedMasterSubject(value || '');
                            setSelectedMasterChapter('');
                            setSelectedMasterTopic('');
                        }}
                    />

                    {(selectedMasterSubject || questionDetails?.question?.masterSubjectId) && (
                        <MasterDataAutoComplete
                            key={`chapter-${questionDetails?.question?.masterChapterId || 'empty'}`}
                            type="chapter"
                            filterId={
                                selectedMasterSubject || questionDetails?.question?.masterSubjectId
                            }
                            onChange={(value: any) => {
                                setSelectedMasterChapter(value || '');
                                setSelectedMasterTopic('');
                            }}
                        />
                    )}

                    {(selectedMasterSubject || questionDetails?.question?.masterSubjectId) &&
                        (selectedMasterChapter || questionDetails?.question?.masterChapterId) && (
                            <MasterDataAutoComplete
                                key={`topic-${questionDetails?.question?.masterTopicId || 'empty'}`}
                                type="topic"
                                filterId={
                                    selectedMasterChapter ||
                                    questionDetails?.question?.masterChapterId
                                }
                                subjectId={
                                    selectedMasterSubject ||
                                    questionDetails?.question?.masterSubjectId
                                }
                                onChange={(value: any) => setSelectedMasterTopic(value || '')}
                            />
                        )}

                    <Tabs value={value} onChange={handleChange}>
                        {selectedLanguages.map((lang, index) => (
                            <Tab key={lang} label={lang} />
                        ))}
                    </Tabs>

                    {selectedLanguages.map((lang, index) => (
                        <TabPanel key={lang} value={value} index={index}>
                            <LanguageFields language={lang} />
                        </TabPanel>
                    ))}

                    <OptionsFields
                        languages={selectedLanguages}
                        selectedType={questionDetails?.question?.type}
                    />
                </Box>
            </SimpleForm>
        </Box>
    );
};

export default QuestionBankEdit;
